// Web Vitals Monitoring
export const measureWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    }).catch((error) => {
      console.warn('Web Vitals not available:', error);
    });
  }
};

// Performance monitoring utility
export const initializePerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;
  
  // Measure and report web vitals
  measureWebVitals((metric) => {
    // Send to analytics
    if (window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        event_label: metric.id,
        non_interaction: true,
      });
    }
    
    // Log for development
    if (process.env.NODE_ENV === 'development') {
      console.log('Web Vital:', metric);
    }
  });
  
  // Monitor resource loading
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === 'resource' && entry.duration > 1000) {
          // Report slow resources
          if (window.gtag) {
            window.gtag('event', 'slow_resource', {
              event_category: 'Performance',
              event_label: entry.name,
              value: Math.round(entry.duration),
              custom_parameter_1: entry.initiatorType
            });
          }
        }
      });
    });
    
    observer.observe({ entryTypes: ['resource', 'navigation'] });
  }
};

// Image optimization utilities
export const createImageSrcSet = (basePath, sizes = [320, 640, 960, 1280, 1920]) => {
  const extension = basePath.split('.').pop();
  const pathWithoutExt = basePath.replace(`.${extension}`, '');
  
  return sizes.map(size => `${pathWithoutExt}-${size}w.webp ${size}w`).join(', ');
};

// Preload critical resources
export const preloadCriticalResources = (resources) => {
  if (typeof document === 'undefined') return;
  
  resources.forEach(({ href, as, type, crossOrigin }) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    
    if (type) link.type = type;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    
    document.head.appendChild(link);
  });
};

// Lazy loading intersection observer
export const createLazyLoadObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };
  
  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(callback, defaultOptions);
  }
  
  return null;
};

// Critical CSS inlining utility (for build process)
export const inlineCriticalCSS = (html, criticalCSS) => {
  return html.replace(
    '<style id="critical-css"></style>',
    `<style id="critical-css">${criticalCSS}</style>`
  );
};

// Service Worker utilities
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

// Resource hints for better performance
export const addResourceHints = (hints) => {
  if (typeof document === 'undefined') return;
  
  hints.forEach(({ rel, href, crossOrigin }) => {
    const link = document.createElement('link');
    link.rel = rel; // 'dns-prefetch', 'preconnect', 'prefetch'
    link.href = href;
    
    if (crossOrigin) link.crossOrigin = crossOrigin;
    
    document.head.appendChild(link);
  });
};

// Font loading optimization
export const optimizeFontLoading = () => {
  if (typeof document === 'undefined') return;
  
  // Preload critical fonts
  const criticalFonts = [
    '/fonts/inter-var.woff2',
    '/fonts/inter-bold.woff2'
  ];
  
  criticalFonts.forEach(fontUrl => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = fontUrl;
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};